import { ClientManagerInstanceToken } from '../../interfaces/client-manager.interface';
import { ODataObject } from '../../interfaces/odata-model.interface';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { CompositeModel } from '../complex/composite.model';
import { NumberModel } from '../primitive/number.model';
import { RatingModel } from '../primitive/rating.model';
import { TextModel } from '../primitive/text.model';
import { TextModelOptions } from '../options/text-model-options';

/**
 * This model is used for screens MaterialPickup and MaterialReturn to create array of materials
 */
export class MaterialPartSetModel extends CompositeModel {
    public awb: TextModel;
    public materialNumber: TextModel;
    public orderNumber: TextModel;
    public orderPosition: TextModel;
    public quantity: NumberModel;
    public feedbackRating?: RatingModel;
    public feedbackComment?: TextModel;
    public pickupRfid: TextModel;

    public awbPackaging: TextModel;

    public constructor(
        awb = '',
        materialNumber = '',
        orderNumber = '',
        orderPosition = '',
        quantity: number | undefined = 1,
        pickupRfid = '',
        awbPackaging = '',
    ) {
        super('MaterialPartSet');

        const injector = CustomInjector.getInjector();
        const clientManager = injector.get(ClientManagerInstanceToken);
        this.awb = <TextModel>this.set(new TextModel('ReturnLabel', awb));
        this.materialNumber = <TextModel>this.set(new TextModel('Material', materialNumber));
        this.orderNumber = <TextModel>this.set(new TextModel('PONumber', orderNumber));
        this.orderPosition = <TextModel>this.set(new TextModel('POItemNumber', orderPosition));
        this.quantity = <NumberModel>this.set(new NumberModel('Quantity', quantity));
        this.pickupRfid = <TextModel>this.set(
            new TextModel(
                'PickupRfid',
                pickupRfid,
                new TextModelOptions({
                    readonly: pickupRfid.length > 0,
                    required: true,
                }),
            ),
        );

        this.awbPackaging = <TextModel>this.set(new TextModel('PkgAwbnr', awbPackaging));
        if (clientManager.isNgmAAChina()) {
            this.feedbackRating = <RatingModel>this.set(new RatingModel('FeedbackRating'));
            this.feedbackComment = <TextModel>this.set(new TextModel('FeedbackComment'));
        }
    }

    public getOutModel(): ODataMaterialPartSetOutModel {
        const outModel = <ODataMaterialPartSetOutModel>super.getOutModel();

        // convert number to string as expected by gateway
        outModel.Quantity = outModel.Quantity ? outModel.Quantity.toString() : '';
        outModel.UCRNumber = outModel.UCRNumber ? outModel.UCRNumber.toString() : '';

        return outModel;
    }

    /**
     * Only save those value that might have been modified by the user and cannot be restored from some kind of default value
     *
     * @returns {any}
     */
    public toJSON(): unknown {
        return {
            awb: this.awb.value,
            materialNumber: this.materialNumber.value,
            orderNumber: this.orderNumber.value,
            orderPosition: this.orderPosition.value,
            quantity: this.quantity.value,
            feedbackRating: this.feedbackRating ? this.feedbackRating.value : undefined,
            feedbackComment: this.feedbackComment ? this.feedbackComment.value : undefined,
            awbPackaging: this.awbPackaging.value,
        };
    }
}

export interface ODataMaterialPartSetOutModel extends ODataObject {
    ReturnLabel: string;
    Material: string;
    PONumber: string;
    POItemNumber: string;
    Quantity: string;
    FeedbackRating: string;
    FeedbackComment: string;
    UCRNumber: string;
    PkgAwbnr: string;
}
